/**
 * Created by Simon Zhao on 2022/05/30.
 * ------ maintenance history ------
 *
 */
import { IdHelperService } from '../../../services/id-helper.service';
import { TypeUtilities } from '../../../services/utilities/type-utilities';
import { AutomationModelBase } from '../../../tamalelibs/models/automation-base.model';
import {
    ApprovalTaskConfiguration,
    COLORS,
    ConnectionLine,
    GatewayDefinition,
    GatewayType,
    GatewayTypes,
    NodeTypes,
    ProcessDefinition,
    TaskColor,
    TaskType,
    TaskTypes,
} from '../../../tamalelibs/models/workflow.model';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import {
    BranchType,
    DiagramBranch,
    DiagramNode,
    GatewaySuccessorCondition,
} from '../../../widgets/diagram/diagram-service';
import { END_NODE, START_NODE } from '../../../widgets/diagram/diagram.model';
import { DiagramService } from '../../../widgets/diagram/diagram.service';
import { DefaultGatewayInfo } from '../workflow-model';

export class DiagramNodeCompactModel {
    constructor(
        public guid: string,
        public parents: string[],
        public children: string[],
        public name: string,
        public type: any,
        public stage: string,
        public automationConfigs: AutomationModelBase[],
        public condition: string,
        public branchs: DiagramBranch[],
        public id: string,
        public valid: boolean,
        public stageColor?: TaskColor,
        public approvalTask?: ApprovalTaskConfiguration,
        public gatewayConfig?: any[],
        /**
         * This is used at design time to determine whether a node is on a gateway's branch.
         */
        public parentBranchID?: string,
    ) { }
}

const generateBranchGuidFunc = (targetId: string, index: number) =>
    `con_${targetId}_${index}`;
const extractBranchIndexFunc = (branchId: string, branches?: Array<DiagramBranch>) => {
    if (branchId && branchId.length > 0 && branchId.split('_').length === 3) {
        return Number(branchId.split('_')[2]);
    } else if (branches && branches.length > 0) {
        return branches.findIndex(branch => branch.guid && branch.guid === branchId);
    }
    return -1;
};
const isLineBranchMatched = (line: ConnectionLine, branch: DiagramBranch) =>
    line.conditionSpEL === branch.condition &&
    branch.guid.indexOf(line.targetRef) > 0;
const pushNonexistingLineFunc = (parents: Array<string>, nodeId: string, lineCollection: any[], lineIndex: number, diagramService, parentAutomations: Map<string, Array<any>>) => {
    const existingLine = lineCollection.find(line => line.sourceRef === parents.join(',') && line.targetRef === nodeId);
    if (!existingLine) {
        // this line maybe created by the parent already.
        const lineToPush: ConnectionLine = {
            targetRef: nodeId,
            sourceRef: parents.join(','),
            sort: ++lineIndex
        };
        const parentNode = diagramService.getNode(parents.join(','));
        if (parentNode && parentNode.automationConfigs) {
            lineToPush.automation = parentNode.automationConfigs;
        } else if (parentAutomations.has(parents.join(','))) {
            lineToPush.automation = parentAutomations.get(parents.join(','));
        }
        lineCollection.push(lineToPush);
    }
    return lineIndex;
};
// line creation function, create a line between the source and the target, and copy the condition from the branch if the given branch is a conditional branch.
const createLineFunc = (_source: string, _target: string, _branch: DiagramBranch, _index?: number, autoCfgs?: Array<AutomationModelBase>) => {
    const lineToChildNode: ConnectionLine = {
        targetRef: _target,
        sourceRef: _source,
        sort: _index
    };
    if (_branch && _branch.type === BranchType.ConditionalBranch) {
        // for conditional gateway's child nodes, need to copy the condition from the branch to the connection line.
        lineToChildNode.conditionSpEL = _branch.condition;
    }
    if (autoCfgs) {
        lineToChildNode.automation = autoCfgs;
    }

    return lineToChildNode;
};

export class WorkflowConfigUtility {

    static alignGatewaySuccessorWithGrandBranch(gatewayNodeItem: DiagramNodeCompactModel,
        diagramNodeArray: Array<DiagramNodeCompactModel>) {
        if (!DiagramBranch.isBlankCondition(gatewayNodeItem.condition) && gatewayNodeItem.children.length > 0) {
            gatewayNodeItem.children.forEach(childNodeId => {
                const childNode = diagramNodeArray.find(node => node.id === childNodeId);
                if (childNode && DiagramBranch.isBlankCondition(childNode.condition)) {
                    childNode.parentBranchID = gatewayNodeItem.condition;
                    WorkflowConfigUtility.alignGatewaySuccessorWithGrandBranch(childNode, diagramNodeArray);
                }
            });
        }
    }

    static alignBranchesForGateway(
        diagramNodeItem: DiagramNodeCompactModel,
        diagramNodeArray: Array<DiagramNodeCompactModel>,
        configuredNodes: Array<any>,
        conditionalLines: Array<ConnectionLine>,
        allLines: Array<ConnectionLine>
    ) {
        const child = [];
        diagramNodeItem.children.forEach((data, index) => {
            const childNode = diagramNodeArray.find(
                (_node) => _node.id === data
            );
            if (childNode) {
                // parentNode is diagramNodeItem?
                const parentNode = configuredNodes.find(
                    (item1) => item1.id === childNode.parents[0]
                );
                if (
                    parentNode.type === GatewayType.AND ||
                    parentNode.type === GatewayType.SKIP
                ) {
                    // 3.1.1 child node's parent is the end node of a gateway.
                    const parents = new Set(childNode.parents);
                    // 3.1.2 change to use the guid rather than id?
                    parents.delete(parentNode.id);
                    parents.add(diagramNodeItem.guid);
                    childNode.parents = Array.from(parents);
                    childNode.condition = 'none';
                } else {
                    // 3.2.1 child node's parent is a node inside a gateway's branch
                    childNode.condition = generateBranchGuidFunc(
                        childNode.guid,
                        index
                    );
                    // sync the parent node's branch to the attribute used in design time.
                    childNode.parentBranchID = childNode.condition;
                    WorkflowConfigUtility.alignGatewaySuccessorWithGrandBranch(childNode, diagramNodeArray);
                    const branch: DiagramBranch = new DiagramBranch();
                    branch.guid = childNode.condition;
                    if (diagramNodeItem.type === GatewayType.CONDITIONAL) {
                        // For a conditional gateway, both the branch type and the branch condition need to be restored.
                        // 3.2.1.1 find the line connecting between the gateway begin node and current child node
                        branch.type = BranchType.ConditionalBranch;
                        const matchedConditionCfg = conditionalLines.find(
                            (cItem) =>
                                cItem.sourceRef === diagramNodeItem.id &&
                                cItem.targetRef === childNode.id
                        );
                        if (matchedConditionCfg) {
                            // 3.2.1.1.1 copy the condition and automation from the line to the branch.
                            branch.condition =
                                matchedConditionCfg.conditionSpEL;
                            branch.automationConfigs =
                                matchedConditionCfg.automation &&
                                    matchedConditionCfg.automation.length > 0
                                    ? [...matchedConditionCfg.automation]
                                    : null;
                        } else {
                            // 3.2.1.1.2 default the condition of current branch to always-going-through.
                            branch.condition = true;
                        }
                    } else {
                        branch.type = BranchType.NormalBranch;
                    }

                    diagramNodeItem.branchs.push(branch);
                }
                child.push(data);
            }
        });
        diagramNodeItem.children = child;
        if (diagramNodeItem.type === GatewayType.CONDITIONAL) {
            // conditional gateways
            const gatewayDefs: Array<GatewayDefinition> = Array.from(
                diagramNodeItem.gatewayConfig
            );
            if (
                diagramNodeItem.branchs.length === 0 &&
                gatewayDefs &&
                gatewayDefs.length === 2
            ) {
                // 3.2.1 for the conditional gateway without any input/approval task,
                // find out all lines connecting between the gateway's start node and the gateway's end node.
                // create branches based on found lines.
                const branchLines = allLines.filter(
                    (line) =>
                        line.sourceRef === gatewayDefs[0].id &&
                        line.targetRef === gatewayDefs[1].id
                );
                branchLines.forEach((bl) => {
                    const newBranch = new DiagramBranch();
                    newBranch.automationConfigs =
                        bl.automation && bl.automation.length > 0
                            ? [...bl.automation]
                            : null;
                    newBranch.condition = bl.conditionSpEL;
                    newBranch.guid = IdHelperService.uuidv4();
                    newBranch.id = newBranch.guid;
                    newBranch.type = BranchType.ConditionalBranch;
                    diagramNodeItem.branchs.push(newBranch);
                });
            }
            // 3.4 for conditional gateway, branches without any child task could not be rebuilt through the children iteration.
            // so compare the connection lines with the branches created based on the children iteration, and explicitly create
            // a separate branch for those not including any child.
            const conditionLinesFromCurrentGateway = conditionalLines.filter(
                (cItem) => cItem.sourceRef === diagramNodeItem.id
            );
            if (
                conditionLinesFromCurrentGateway.length >
                diagramNodeItem.branchs.length &&
                diagramNodeItem.gatewayConfig &&
                diagramNodeItem.gatewayConfig.length > 1
            ) {
                const skipGatewayId = diagramNodeItem.gatewayConfig[1].id;
                const linesConnectConditionalGatewayNSkipGateway =
                    conditionLinesFromCurrentGateway.filter(
                        (line) => line.targetRef === skipGatewayId
                    );
                linesConnectConditionalGatewayNSkipGateway.forEach(
                    (lineWithoutChild) => {
                        const lineBranch = new DiagramBranch();
                        lineBranch.guid = generateBranchGuidFunc(
                            lineWithoutChild.targetRef,
                            diagramNodeItem.branchs.length
                        );
                        lineBranch.condition = lineWithoutChild.conditionSpEL;
                        lineBranch.type = BranchType.ConditionalBranch;
                        lineBranch.automationConfigs =
                            lineWithoutChild.automation &&
                                lineWithoutChild.automation.length > 0
                                ? [...lineWithoutChild.automation]
                                : null;
                        diagramNodeItem.branchs.push(lineBranch);
                    }
                );
                const sortedBranches = [];
                // make sure the order of branches of current conditional gateway is same with the order of their corresponding connection lines.
                // Here's an assumption that the child nodes of conditional gateway are ordered.
                conditionLinesFromCurrentGateway.forEach((line) => {
                    const branch = Array.from(diagramNodeItem.branchs).find(
                        (_branch: DiagramBranch) =>
                            isLineBranchMatched(line, _branch)
                    );
                    if (branch) {
                        sortedBranches.push(branch);
                    }
                });

                diagramNodeItem.branchs = sortedBranches;
            }
        } else if (diagramNodeItem.type === GatewayType.PARALLEL) {
            // parallel gateways
            // 3.2.2 for parallel gateway, it has to have at least two branches.
            while (diagramNodeItem.branchs.length < 2) {
                diagramNodeItem.branchs.push({
                    guid: IdHelperService.uuidv4(),
                });
            }
        }
    }

    static alignLinesWithBranches(nodeItem: DiagramNode, diagramService: DiagramService, lineIndex: number, connectionLine: Array<ConnectionLine>) {
        const gatewayInfo = DefaultGatewayInfo[nodeItem.type];
        const startGateway = nodeItem.gatewayConfig.find(data => data.type === gatewayInfo[0].type);
        const endGateway: GatewayDefinition = nodeItem.gatewayConfig.find(data => data.type === gatewayInfo[1].type);
        const tempArr = [];
        const lineEndPointId = endGateway.id;
        if (nodeItem.type === GatewayType.CONDITIONAL) {
            // for conditional gateways, if the child nodes are more than the branches, there might be at least one branch without any sub-task-node.
            // create a connection line between the conditional gateway node and the skip gateway node to persist this branch.
            const gatewayTaskChildrenGuidArray = Array.from(nodeItem.children);

            Array.from(nodeItem.branchs).forEach((branch: DiagramBranch) => {
                const branchChildGuid = gatewayTaskChildrenGuidArray.find(guid => {
                    const tempNode = diagramService.getNode(guid);
                    return tempNode && tempNode.condition === branch.guid;
                });

                if (!branchChildGuid) {
                    // No task children found on current branch, in other words it's a line connecting between the conditional gateway and skip gateway.
                    const lineToPush: ConnectionLine = {
                        targetRef: endGateway.id,
                        sourceRef: startGateway.id,
                        conditionSpEL: branch.condition,
                        sort: ++lineIndex
                    };
                    if (branch.automationConfigs) {
                        lineToPush.automation = branch.automationConfigs;
                    }
                    connectionLine.push(lineToPush);
                } else {
                    // found task child and create corresponding connection line.
                    const branchChildNode = diagramService.getNode(branchChildGuid);
                    if (branchChildNode.condition !== DiagramNode.getNoneCondition()) {
                        // this branch has a connected task, need to create a line between the connected task and the gateway.

                        // push gateway start line for the node with the condition configured.
                        if (TaskTypes.indexOf(branchChildNode.type) > -1) {
                            // for child task nodes of current node.
                            // create a connection line with the source of the id of the gateway's start node and the target of the id of one of current node's child.
                            connectionLine.push(createLineFunc(startGateway.id, branchChildNode.id, branch, ++lineIndex, branch.automationConfigs));
                        } else if (GatewayTypes.indexOf(branchChildNode.type) > -1) {
                            // for gateway child nodes of current gateway node.
                            const tempNodeGatewayInfo = DefaultGatewayInfo[branchChildNode.type];
                            const tempStartGateway = branchChildNode.gatewayConfig.find(data => data.type === tempNodeGatewayInfo[0].type);
                            // create a connection line with the source of the id of current gateway node's start node and the target of the id of the child gateway node's start node.
                            connectionLine.push(createLineFunc(startGateway.id, tempStartGateway.id, branch, ++lineIndex, branch.automationConfigs));
                        }
                        WorkflowConfigUtility.findFirstLeafChild(branchChildNode, diagramService, tempArr);
                    }
                }
            });
        } else {
            // other gateways like parallel gateways.
            const childrenList = [];
            nodeItem.children.forEach(cID => {
                const cNode = diagramService.getNode(cID);
                childrenList.push({ node: cNode, branchIndex: extractBranchIndexFunc(cNode.condition, nodeItem.branchs) });
            });
            ArrayHelperService.sort<{ node: {}, branchIndex: number }>(childrenList, 'branchIndex');
            childrenList.forEach(cNodeItem => {
                const tempNode = cNodeItem.node;
                // only for branch child node, not include not branch child.
                if (tempNode.condition !== DiagramNode.getNoneCondition()) {
                    // push gateway start line
                    if (TaskTypes.indexOf(tempNode.type) > -1) {
                        connectionLine.push({
                            targetRef: tempNode.id,
                            sourceRef: startGateway.id,
                            sort: ++lineIndex
                        });
                    } else if (GatewayTypes.indexOf(tempNode.type) > -1) {
                        const tempStartGateway = tempNode.gatewayConfig.find(data => data.type === DefaultGatewayInfo[tempNode.type][0].type);
                        connectionLine.push({
                            targetRef: tempStartGateway.id,
                            sourceRef: startGateway.id,
                            sort: ++lineIndex
                        });
                    }
                    WorkflowConfigUtility.findFirstLeafChild(tempNode, diagramService, tempArr);
                }
            });
        }

        // push gateway end line
        tempArr.forEach(element => {
            if (TaskTypes.indexOf(element.type) > -1) {
                if (endGateway) {
                    const lineToChildNode: ConnectionLine = {
                        targetRef: endGateway.id,
                        sourceRef: element.id,
                        sort: ++lineIndex
                    };
                    const srcNode = diagramService.getNode(element.id);
                    if (srcNode && srcNode.automationConfigs) {
                        lineToChildNode.automation = srcNode.automationConfigs;
                    }

                    connectionLine.push(lineToChildNode);
                }
            } else if (GatewayTypes.indexOf(element.type) > -1) {
                const elementGatewayInfo = DefaultGatewayInfo[element.type];
                const tempEndGateway = element.gatewayConfig.find(data => data.type === elementGatewayInfo[1].type);
                if (endGateway && tempEndGateway) {
                    const lineToChildNode: ConnectionLine = {
                        targetRef: endGateway.id,
                        sourceRef: tempEndGateway.id,
                        sort: ++lineIndex
                    };
                    let srcNode = diagramService.getNode(tempEndGateway.id);
                    if (element.automationConfigs) {
                        lineToChildNode.automation = element.automationConfigs;
                    } else {
                        // Is there a automation in the endline.
                        if (srcNode && srcNode.automationConfigs) {
                            lineToChildNode.automation = srcNode.automationConfigs;
                        } else {
                            // Is there a automation in the PARALLEL/CONDITIONAL gateway. Need to attach the automation to the endline.
                            srcNode = diagramService.getNode(tempEndGateway.source);
                            if (srcNode && srcNode.automationConfigs) {
                                lineToChildNode.automation = srcNode.automationConfigs;
                            }
                        }
                    }
                    connectionLine.push(lineToChildNode);
                }
            }
        });
        return { lineIndex: lineIndex, lineEndPointId: lineEndPointId };
    }

    static collectParentAutomations(nodeItem: DiagramNode, diagramService: DiagramService, parentAutomations: Map<string, Array<any>>) {
        const parentIds: Array<string> = [];
        let startTask;
        nodeItem.parents.forEach(parentId => {
            const parentNode = diagramService.getNode(parentId);
            if (parentNode) {
                // found parent node, either a task node or a gateway node.
                // used for caching the automation config with the corresponding skip gateway id.
                // as the skip gateway id is virtual and can not find the automation config of gateways.
                let parentIdForLine = parentNode.id;
                if (TaskTypes.indexOf(parentNode.type) > -1) {
                    // a direct connection with a task node.
                    parentIds.push(parentNode.id);
                } else if (GatewayTypes.indexOf(parentNode.type) > -1) {
                    // a connection via gateway, need to dig into the gateway.
                    const gatewayInfo = DefaultGatewayInfo[parentNode.type.toString()];
                    const startGateway = parentNode.gatewayConfig.find(data => data.type === gatewayInfo[0].type);
                    const endGateway = parentNode.gatewayConfig.find(data => data.type === gatewayInfo[1].type);
                    if (startGateway && DiagramBranch.isSuccessorCondition(nodeItem.condition, startGateway.id)) {
                        if (endGateway) {
                            parentIds.push(endGateway.id);
                            parentIdForLine = endGateway.id;
                        }
                    } else {
                        if (startGateway) {
                            parentIds.push(startGateway.id);
                            parentIdForLine = startGateway.id;
                        }
                    }
                }
                if (parentNode.automationConfigs) {
                    parentAutomations.set(parentIdForLine, parentNode.automationConfigs);
                }
            } else {
                // a top level node without parent
                if (TaskTypes.indexOf(nodeItem.type) > -1) {
                    // a task node found, save it as the start task.
                    startTask = nodeItem.id;
                } else if (GatewayTypes.indexOf(nodeItem.type) > -1) {
                    // a gateway node found, find the start node of the gateway and make it as the start task.
                    const gatewayInfo = DefaultGatewayInfo[nodeItem.type];
                    const tempStartGateway = nodeItem.gatewayConfig.find(data => data.type === gatewayInfo[0].type);
                    startTask = tempStartGateway.id;
                }
            }
        });

        return { startTask: startTask, parentIds: parentIds };
    }

    static findFirstLeafChild(parentNode: DiagramNode, diagramService: DiagramService, leafNodes: Array<DiagramNode>) {
        if (parentNode.children.size > 0) {
            if (TaskTypes.indexOf(parentNode.type) > -1) {
                parentNode.children.forEach(element => {
                    const childNode = diagramService.getNode(element);
                    if (childNode) {
                        WorkflowConfigUtility.findFirstLeafChild(childNode, diagramService, leafNodes);
                    }
                });
            } else if (GatewayTypes.indexOf(parentNode.type) > -1) {
                // gateway children not the begining node of any branch.
                const tempChild = [];
                // use the child not in the line, if the not line child is null, use pateway end id
                parentNode.children.forEach(child => {
                    const tempNode = diagramService.getNode(child);
                    // determine whether current child is on any branch of the gateway
                    if (!parentNode.branchs.find(item => item.guid === tempNode.condition) && tempNode) {
                        tempChild.push(child);
                    }
                });
                if (tempChild.length !== 0) {
                    // current gateway has a follow-up node, keep digging in.
                    tempChild.forEach(element => {
                        const tempNode = diagramService.getNode(element);
                        WorkflowConfigUtility.findFirstLeafChild(tempNode, diagramService, leafNodes);
                    });
                } else {
                    // current gateway has no follow-up so it might be the last node?
                    leafNodes.push(parentNode);
                }
            }
        } else {
            // found a leaf node.
            leafNodes.push(parentNode);
        }
    }

    /**
     * Gets the color config for the given stage.
     * @param stageIndex the stage index
     * @returns the color config for the given stage.
     */
    static getStageColor(stageIndex: number): TaskColor {
        const defaultTaskColorFunc = () => {
            const backColor = COLORS[0] + 26;
            const fontColor = TypeUtilities.hexToRGB(COLORS[0]);
            return new TaskColor(backColor, fontColor);
        };
        try {
            if (stageIndex >= 0) {
                if (stageIndex >= COLORS.length) {
                    stageIndex = stageIndex % COLORS.length;
                }

                const backColor = COLORS[stageIndex] + 26;
                const fontColor = TypeUtilities.hexToRGB(COLORS[stageIndex]);
                return new TaskColor(backColor, fontColor);
            } else {
                return defaultTaskColorFunc();
            }
        } catch (e) {
            return defaultTaskColorFunc();
        }
    }

    static generateCompactNodeForRegularTask(
        cfgNode: any,
        parents: Set<string>,
        children: Set<string>,
        isDraft: boolean
    ): DiagramNodeCompactModel {
        const node: DiagramNodeCompactModel = {
            guid: cfgNode.id,
            parents: Array.from(parents),
            children: Array.from(children),
            name: cfgNode.name,
            type: cfgNode.type,
            stage: cfgNode.stage ? cfgNode.stage.id : '',
            automationConfigs: cfgNode.automation,
            condition: '',
            branchs: [],
            id: cfgNode.id,
            valid: isDraft ? !!cfgNode.valid : true,
        };
        if (cfgNode.stageColor || cfgNode.stageColor === 0) {
            node.stageColor = WorkflowConfigUtility.getStageColor(
                cfgNode.stageColor
            );
        }

        if (node.type === TaskType.APPROVAL) {
            // append additional properties for approval nodes.
            node.approvalTask = new ApprovalTaskConfiguration();
            node.approvalTask.type = cfgNode.approvalTask.type;
        }

        return node;
    }

    static generateCompactNodeForGateway(
        cfgNode: any,
        configuredNodes: any[],
        workflowService: WorkflowService,
        workflow: ProcessDefinition,
        parents: Set<string>,
        children: Set<string>,
        isDraft: boolean
    ) {
        const lines: ConnectionLine[] = workflow.configuration.connectionLine;
        const nextStepLine: ConnectionLine = lines.find(
            (line) => line.sourceRef === cfgNode.id
        );
        // obtain the target node id from the line.
        const nextStepTargetRef = !nextStepLine ? '' : nextStepLine.targetRef;
        // 2.3.2.2 further dig into the destination of the connection line, especially for the case in which the gateway is the target of this line.
        const endNodeId =
            workflowService.getEndTaskNodeIdAtEndOfOneParallelLineOrTarget(
                nextStepTargetRef,
                null,
                workflow
            );
        const gatewayEndNodeId = endNodeId ? endNodeId : cfgNode.target;
        const endNode = configuredNodes.find((data) => data.id === gatewayEndNodeId);
        // 2.3.2.3 iterate connection lines
        lines.forEach((line) => {
            if (line.sourceRef === endNodeId) {
                // 2.3.2.3.1 find the line begins with the target node, and add the target of the found line to the child node list of current node.
                children.add(line.targetRef);
                if (line.automation && line.automation.length > 0) {
                    // 2.3.2.3.2 copy automation configs from the line to current node.
                    cfgNode.automation = [...line.automation];
                }
            }
        });
        // 2.3.2.4 prepare gateway configs for current gateway node.
        const gatewayConfig = [cfgNode];
        if (endNode) {
            // push the node connected with current gateway node to the config right after the gateway itself.
            gatewayConfig.push(endNode);
        }
        const node: DiagramNodeCompactModel = {
            guid: cfgNode.id,
            id: cfgNode.id,
            parents: Array.from(parents),
            children: Array.from(children),
            name: `branch_${cfgNode.id}_name`,
            type: cfgNode.type,
            condition: '',
            branchs: [],
            stage: '',
            valid: isDraft ? !!cfgNode.valid : true,
            gatewayConfig: gatewayConfig,
            automationConfigs: cfgNode.automation
                ? [...cfgNode.automation]
                : null,
        };
        return node;
    }

    /**
     * Loads the connection lines from the process definition and create diagram nodes based on them.
     * @param workflow the process definition including connection lines
     * @param isDraft a flag indicating whether is a workflow draft.
     * @param workflowService an instance of workflow service.
     * @returns an array of diagram nodes.
     */
    static loadConnectionLines(
        workflow: ProcessDefinition,
        isDraft: boolean,
        workflowService: WorkflowService
    ): Array<DiagramNodeCompactModel> {
        // sort connection lines with attribute sort.
        ArrayHelperService.sort<ConnectionLine>(
            workflow.configuration.connectionLine,
            'sort'
        );
        // an array of lines connecting with a conditionnal gateway.
        const conditionalLines = [];
        const connectionLines = workflow.configuration.connectionLine;
        connectionLines.forEach((line) => {
            if (line.conditionSpEL) {
                conditionalLines.push(line);
            }
        });
        const gatewayDefs = workflow.gatewayDefinition;
        const taskDefs = workflow.taskDefinition;
        // 1. Extract all node ids including tasks and gateways from the process definition.
        const configuredNodes = [];
        if (gatewayDefs && gatewayDefs.length > 0) {
            configuredNodes.push(...gatewayDefs);
        }
        if (taskDefs && taskDefs.length > 0) {
            configuredNodes.push(...taskDefs);
        }
        const diagramNodesMap = new Map<string, DiagramNodeCompactModel>();
        // 2. iterate configured nodes and find parents and children of each node and push the one with the parent and children in the map.
        configuredNodes.forEach((cfgNode) => {
            const parentIDs = new Set<string>();
            const childrenIDs = new Set<string>();
            // 2.1 iterate connection lines.
            connectionLines.forEach((conLine) => {
                if (conLine.sourceRef === cfgNode.id) {
                    // 2.1.1 found the line begins with current node.
                    const lineTargetNode = configuredNodes.find(
                        (node) => node.id === conLine.targetRef
                    );
                    if (
                        lineTargetNode &&
                        lineTargetNode.type !== GatewayType.AND &&
                        lineTargetNode.type !== GatewayType.SKIP
                    ) {
                        //  the functional nodes like AND and SKIP are excluded as the child nodes.
                        // 2.1.1.1 add the target node to the child node list of current node.
                        childrenIDs.add(conLine.targetRef);
                    }
                    if (
                        !GatewayTypes.includes(cfgNode.type) &&
                        conLine.automation &&
                        conLine.automation.length > 0
                    ) {
                        // only take the non-gateway node's automation config when the line begins with the gateway's id.
                        // 2.1.1.2 extract automation config from the line.
                        cfgNode.automation = [...conLine.automation];
                    }
                }

                if (conLine.targetRef === cfgNode.id) {
                    // 2.1.2 found the line ends with current node.
                    // 2.1.2.1 add the source node to the parent node list of current node.
                    parentIDs.add(conLine.sourceRef);
                }
            });

            // 2.2 check if there's a parent of current node.
            if (parentIDs.size === 0) {
                // for the node without any parent found, treat the start node as its only parent.
                // 2.2.1 set start node as the parent of current node.
                parentIDs.add(START_NODE.guid);
            }

            // 2.3 Init the diagram node for current node. If current node is the end node of gateways like skip or and, it would be skipped here.
            if (TaskTypes.indexOf(cfgNode.type) > -1) {
                // 2.3.1 current node is a *task node*, i.e either an approval node or an input node.
                // push the new node instance created with parents and children for current node to the diagram node map.
                diagramNodesMap.set(
                    cfgNode.id,
                    WorkflowConfigUtility.generateCompactNodeForRegularTask(
                        cfgNode,
                        parentIDs,
                        childrenIDs,
                        isDraft
                    )
                );
            } else if (NodeTypes.indexOf(cfgNode.type) > -1) {
                // 2.3.2 current node is a *gateway node* i.e parallel, exclusive or conditional.
                // 2.3.2.1 find the line begins with current gateway node.
                // push the new node instance with the gateway config to the diagram node map.
                const gatewayNode =
                    WorkflowConfigUtility.generateCompactNodeForGateway(
                        cfgNode,
                        configuredNodes,
                        workflowService,
                        workflow,
                        parentIDs,
                        childrenIDs,
                        isDraft
                    );
                diagramNodesMap.set(cfgNode.id, gatewayNode);
            }
        });

        // 3. iterate diagram nodes
        const diagramNodeArray = Array.from(diagramNodesMap.values());

        diagramNodeArray.forEach((diagramNodeItem) => {
            // iterate the node instances in the diagram map.
            if (NodeTypes.indexOf(diagramNodeItem.type) > -1) {
                // 3.1 the gateway node instances, iterate the child nodes of current gateway node to restore branches.
                WorkflowConfigUtility.alignBranchesForGateway(
                    diagramNodeItem,
                    diagramNodeArray,
                    configuredNodes,
                    conditionalLines,
                    connectionLines
                );
            }
        });

        return diagramNodeArray;
    }

    /**
     * Creates connection lines based on current diagram nodes.
     * @param diagramService an instance of the diagram service.
     * @returns an object includes the connection lines and the start task.
     */
    static saveAsConnnectionLines(diagramService: DiagramService): { connectionLine: Array<ConnectionLine>, startTask: string } {
        const nodes = diagramService.serialize();
        const connectionLine: Array<ConnectionLine> = [];
        let startTask;
        let lineIndex = 0;


        nodes.forEach(nodeItem => {
            const parentIds = [];
            const parentAutomations = new Map<string, Array<any>>();
            // default to node's id, for a gateway node, it would be gateway's end's id.
            let lineEndPointId = nodeItem.id;
            const parentIdsNStTask = WorkflowConfigUtility.collectParentAutomations(nodeItem, diagramService, parentAutomations);
            parentIds.push(...parentIdsNStTask.parentIds);
            if (parentIdsNStTask.startTask) {
                startTask = parentIdsNStTask.startTask;
            }

            if (parentIds.length > 0) {
                // current node is not the start node.
                // create the line between the parent and current node.
                if (TaskTypes.indexOf(nodeItem.type) > -1 && !diagramService.isGatewayStartNode(parentIds.join(','))) {
                    // do NOT create the creation of lines connecting between gateway's start node and branch children.
                    // create a connection line with the source of an id array of parent nodes and the target of current node's id.
                    lineIndex = pushNonexistingLineFunc(parentIds, nodeItem.id, connectionLine, lineIndex, diagramService, parentAutomations);
                } else if (GatewayTypes.indexOf(nodeItem.type) > -1) {
                    const gatewayInfo = DefaultGatewayInfo[nodeItem.type];
                    const tempStartGateway = nodeItem.gatewayConfig.find(data => data.type === gatewayInfo[0].type);
                    if (tempStartGateway) {
                        // create a connection line with the source of an id array of parent nodes and the target of the id of the gateway's start node.
                        lineIndex = pushNonexistingLineFunc(parentIds, tempStartGateway.id, connectionLine, lineIndex, diagramService, parentAutomations);
                    }
                }
            }

            if (GatewayTypes.indexOf(nodeItem.type) > -1) {
                // current node is a gateway, more things need to do like handling branches.
                const lineEndPointIdNIndex = WorkflowConfigUtility.alignLinesWithBranches(nodeItem, diagramService, lineIndex, connectionLine);
                lineIndex = lineEndPointIdNIndex.lineIndex;
                lineEndPointId = lineEndPointIdNIndex.lineEndPointId;
            }

            if (nodeItem.isLastNode && lineEndPointId) {
                // explicitly create a line connecting between the last node and the end node.
                const endLine = new ConnectionLine();
                endLine.sourceRef = lineEndPointId;
                endLine.targetRef = END_NODE.id;
                endLine.sort = ++lineIndex;
                if (nodeItem.automation && Array.isArray(nodeItem.automation) && nodeItem.automation.length > 0) {
                    endLine.automation = [...nodeItem.automation];
                }
                connectionLine.push(endLine);
            }
        });
        // for conditional gateway lines, the lines connect between conditional gateway and skip gateway may need more than one, but each of them should have a sparate condition config
        const tempLines = [];
        ArrayHelperService.sort<ConnectionLine>(connectionLine, 'sort');
        let index = 0;
        connectionLine.forEach(item => {
            if (!tempLines.find(line => line.targetRef === item.targetRef && line.sourceRef === item.sourceRef
                && line.conditionSpEL === item.conditionSpEL)) {
                item.sort = ++index;
                tempLines.push(item);
            }
        });

        return {
            connectionLine: tempLines,
            startTask: startTask
        };
    }
}
